import { useEffect } from 'react';
import { usePublicBranding } from './useSettings';
import { resolveBranding } from '@/lib/color/contrast';

/**
 * Hook to apply company branding to the page
 * Updates document title and favicon based on company settings
 * Uses the public branding endpoint so it works on login page too
 */
export function usePageBranding() {
  const { data: branding } = usePublicBranding();

  useEffect(() => {
    if (!branding) return;

    // Update document title
    const appName = branding.app_name || 'Antenneservice';
    document.title = appName;

    // Update favicon if custom favicon uploaded
    if (branding.favicon_url) {
      updateFavicon(branding.favicon_url);
    }

    // Drive the primary brand colour from settings (falls back safely to the
    // butter-yellow token default, with a contrast guard — see resolveBranding).
    applyBrandColor(branding.brand_color);
  }, [branding]);
}

/**
 * Apply the operator's configured brand colour to the design-token layer at
 * runtime. Only the primary FILL tokens (and, when the colour is readable on
 * white, the interactive-ink token) are touched — semantic success/warning/
 * info/danger colours are never overridden. Invalid or unusable colours fall
 * back to the butter-yellow token default.
 */
function applyBrandColor(brandColor: string | null | undefined): void {
  const root = document.documentElement;
  const { primaryHsl, primaryForegroundHsl, accentSoftHex, accentInkHex } =
    resolveBranding(brandColor);

  // Shadcn primary fill (buttons, badges, selection) + its readable foreground.
  root.style.setProperty('--primary', primaryHsl);
  root.style.setProperty('--primary-foreground', primaryForegroundHsl);
  // Custom-token layer: soft brand tint + readable interactive/link ink.
  root.style.setProperty('--accent-soft', accentSoftHex);
  root.style.setProperty('--accent-c', accentInkHex);
}

/**
 * Hook to set a specific page title while maintaining app branding
 * @param pageTitle - The specific page title (e.g., "Customers", "Invoice #123")
 */
export function usePageTitle(pageTitle?: string) {
  const { data: branding } = usePublicBranding();

  useEffect(() => {
    const appName = branding?.app_name || 'Antenneservice';

    if (pageTitle) {
      document.title = `${pageTitle} - ${appName}`;
    } else {
      document.title = appName;
    }

    // Cleanup: reset to app name when component unmounts
    return () => {
      document.title = appName;
    };
  }, [pageTitle, branding?.app_name]);
}

/**
 * Updates the favicon link element with a new URL
 */
function updateFavicon(faviconUrl: string) {
  // Find existing favicon link or create new one
  let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");

  if (!link) {
    link = document.createElement('link');
    link.rel = 'shortcut icon';
    document.head.appendChild(link);
  }

  link.type = 'image/x-icon';
  link.href = faviconUrl;
}
